<template>
  <main class="sf-page">
    <div class="sf-page-hero">
      <div class="sf-page-hero__inner" data-aos="fade-up">
        <p class="sf-page-hero__eyebrow"><span class="sf-page-hero__eyebrow-dot"></span>Boostez votre carrière</p>
        <h1 class="sf-h1">Formations <em>IT</em></h1>
        <p class="sf-page-hero__sub">Des programmes de qualité pour vous préparer aux métiers du numérique.</p>
        <div class="fm-badges">
          <span>100% Pratique</span><span>Formateurs Experts</span><span>Certification</span>
        </div>
      </div>
    </div>

    <div v-if="loading" class="sf-loader"><span class="sf-loader__ring"></span></div>
    <template v-else>
      <!-- Filters -->
      <section class="sf-section sf-section--cream" style="padding-bottom:0">
        <div class="sf-container">
          <div class="fm-filter-row">
            <div class="sf-filters" style="margin-bottom:0">
              <button class="sf-pill" :class="{'sf-pill--active':selectedLevel===''}" @click="filterByLevel('')">Tous niveaux</button>
              <button v-for="lv in ['Débutant','Intermédiaire','Avancé']" :key="lv" class="sf-pill" :class="{'sf-pill--active':selectedLevel===lv}" @click="filterByLevel(lv)">{{ lv }}</button>
            </div>
            <div class="sf-search-wrap"><Search /><input v-model="searchQuery" @input="debouncedSearch" placeholder="Rechercher…" class="sf-input" style="max-width:220px" /></div>
          </div>
        </div>
      </section>

      <!-- Grid -->
      <section class="sf-section sf-section--cream">
        <div class="sf-container">
          <div v-if="!formations.length" class="sf-empty"><Search class="sf-empty__icon" /><p class="sf-empty__title">Aucune formation trouvée</p></div>
          <div v-else class="fm-grid">
            <div v-for="(fm, i) in formations" :key="fm.id" class="fm-card" data-aos="fade-up" :data-aos-delay="i*80">
              <div class="fm-card__head">
                <div class="fm-card__icon-wrap"><component :is="getIconComponent(fm.icon)" /></div>
                <div>
                  <h3 class="fm-card__title">{{ fm.title }}</h3>
                  <div class="fm-card__badges">
                    <span class="fm-badge">{{ fm.level }}</span>
                    <span class="fm-badge fm-badge--muted">{{ fm.duration }}</span>
                    <span v-if="fm.status==='complet'" class="fm-badge fm-badge--red">Complet</span>
                  </div>
                </div>
              </div>
              <p class="fm-card__desc">{{ fm.description }}</p>
              <div class="fm-card__info">
                <div class="sf-info-row" style="gap:.5rem"><div style="width:24px;display:flex;align-items:center;justify-content:center"><Calendar style="width:14px;height:14px;color:var(--terra)" /></div><div><p class="sf-info-row__label">Prochaine session</p><p class="sf-info-row__value" style="font-size:.85rem">{{ formatDate(fm.next_session) }}</p></div></div>
                <div class="sf-info-row" style="gap:.5rem"><div style="width:24px;display:flex;align-items:center;justify-content:center"><Users style="width:14px;height:14px;color:var(--terra)" /></div><div><p class="sf-info-row__label">Places disponibles</p><p class="sf-info-row__value" style="font-size:.85rem">{{ fm.remaining_spots||fm.capacity-fm.enrolled }} / {{ fm.capacity }}</p></div></div>
              </div>
              <div v-if="fm.enrolled>0" class="sf-progress" style="margin-bottom:1rem">
                <div class="sf-progress__track"><div class="sf-progress__fill" :style="{'--target':(fm.progress||Math.round(fm.enrolled/fm.capacity*100))+'%'}"></div></div>
                <span class="sf-progress__pct">{{ fm.progress||Math.round(fm.enrolled/fm.capacity*100) }}%</span>
              </div>
              <div v-if="fm.skills?.length" class="fm-skills">
                <span v-for="sk in fm.skills.slice(0,3)" :key="sk" class="fm-skill">{{ sk }}</span>
                <span v-if="fm.skills.length>3" class="fm-skill fm-skill--more">+{{ fm.skills.length-3 }}</span>
              </div>
              <div class="fm-card__actions">
                <router-link :to="`/formation/${fm.slug||fm.id}`" style="flex:1"><button class="sf-btn sf-btn--outline sf-btn--full sf-btn--sm">En savoir plus</button></router-link>
                <router-link :to="`/formations/${fm.slug||fm.id}#inscription`" style="flex:1" :class="{'pointer-events-none opacity-50':fm.status==='complet'}">
                  <button class="sf-btn sf-btn--terra sf-btn--full sf-btn--sm" :disabled="fm.status==='complet'">{{ fm.status==='complet'?'Complet':"S'inscrire" }}</button>
                </router-link>
              </div>
            </div>
          </div>

          <div v-if="pagination.last_page>1" class="sf-pagination">
            <button class="sf-page-btn" :disabled="pagination.current_page===1" @click="changePage(pagination.current_page-1)"><ChevronLeft /></button>
            <button v-for="p in displayedPages" :key="p" class="sf-page-btn" :class="{'sf-page-btn--active':pagination.current_page===p}" @click="changePage(p)">{{ p }}</button>
            <button class="sf-page-btn" :disabled="pagination.current_page===pagination.last_page" @click="changePage(pagination.current_page+1)"><ChevronRight /></button>
          </div>
        </div>
      </section>

      <!-- Why us -->
      <section class="sf-section sf-section--forest">
        <div class="sf-container">
          <div class="sf-section-hdr" data-aos="fade-up"><span class="sf-label sf-label--light">Pourquoi nous choisir</span><h2 class="sf-h2 sf-h2--light">L'excellence au <em>service</em> de votre avenir</h2></div>
          <div class="fm-why-grid">
            <div v-for="(w,i) in whyItems" :key="i" class="fm-why-card" data-aos="fade-up" :data-aos-delay="i*100">
              <div class="fm-why-icon"><component :is="w.icon" /></div>
              <h3>{{ w.title }}</h3>
              <p>{{ w.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section class="sf-section sf-section--cream">
        <div class="sf-container">
          <div class="sf-cta-banner" data-aos="fade-up">
            <p class="sf-cta-banner__title">Pas la formation <em>idéale</em> ?</p>
            <p class="sf-cta-banner__sub">Contactez-nous pour discuter de vos besoins. Nous proposons des formations sur mesure pour les entreprises et associations.</p>
            <router-link to="/contact"><button class="sf-btn sf-btn--amber">Nous contacter</button></router-link>
          </div>
        </div>
      </section>
    </template>
    <Transition name="toast"><div v-if="toastMessage" class="sf-toast"><CheckCircle />{{ toastMessage }}</div></Transition>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import AOS from 'aos'; import 'aos/dist/aos.css'
import { Search, Calendar, Clock, Users, Code, Database, Shield, Palette, ArrowRight, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-vue-next'
export default {
  name:'FormationsPage',
  components:{ Search, Calendar, Clock, Users, Code, Database, Shield, Palette, ArrowRight, ChevronLeft, ChevronRight, CheckCircle },
  data(){return{
    searchQuery:'', selectedLevel:'', displayedPages:[], toastMessage:'',
    whyItems:[{icon:'Users',title:'Encadrement personnalisé',desc:'Petits groupes avec un suivi individualisé par nos formateurs experts.'},{icon:'Code',title:'Apprentissage pratique',desc:'Projets concrets et études de cas pour une mise en pratique immédiate.'},{icon:'Shield',title:'Certification reconnue',desc:'Certificats de compétences reconnus par les entreprises du secteur.'}]
  }},
  computed:{...mapState({formations:s=>s.formations,loading:s=>s.loading,pagination:s=>s.formationsPagination})},
  watch:{'pagination.last_page':{handler(){this.updateDisplayedPages()},immediate:true},toastMessage(v){if(v)setTimeout(()=>{this.toastMessage=''},3000)}},
  created(){this.debouncedSearch=debounce(this.handleSearch,500);this.loadFormations()},
  mounted(){AOS.init({once:true,offset:70,easing:'ease-out-cubic',duration:650})},
  methods:{
    ...mapActions(['fetchPublicFormations','setFormationFilter','setFormationPage']),
    async loadFormations(){try{await this.fetchPublicFormations()}catch(e){this.toastMessage='Erreur de chargement'}},
    handleSearch(){this.setFormationFilter({key:'search',value:this.searchQuery});this.setFormationPage(1);this.loadFormations()},
    filterByLevel(l){this.selectedLevel=l;this.setFormationFilter({key:'level',value:l});this.setFormationPage(1);this.loadFormations()},
    changePage(p){if(p>=1&&p<=this.pagination.last_page){this.setFormationPage(p);this.loadFormations();window.scrollTo({top:0,behavior:'smooth'})}},
    updateDisplayedPages(){const c=this.pagination.current_page,l=this.pagination.last_page,d=2;let pg=[];for(let i=1;i<=l;i++){if(i===1||i===l||(i>=c-d&&i<=c+d))pg.push(i)}this.displayedPages=pg},
    getIconComponent(n){return{Code,Database,Shield,Palette}[n]||Code},
    formatDate(d){return d?new Date(d).toLocaleDateString('fr-FR',{day:'2-digit',month:'long',year:'numeric'}):'N/A'}
  }
}
</script>
<style>@import './sf-design.css';</style>
<style scoped>
.fm-badges{display:flex;flex-wrap:wrap;gap:.5rem;justify-content:center;margin-top:1.5rem}
.fm-badges span{background:rgba(255,255,255,.12);color:var(--cream);font-size:.75rem;letter-spacing:.1em;text-transform:uppercase;padding:.35rem .9rem;border-radius:999px;border:1px solid rgba(255,255,255,.2)}
.fm-filter-row{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:1rem;margin-bottom:2rem}
.fm-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:1.5rem}
.fm-card{background:var(--white);border-radius:8px;padding:2rem 1.75rem;box-shadow:var(--shadow-sm);display:flex;flex-direction:column;gap:1rem;transition:transform .3s ease,box-shadow .3s ease}
.fm-card:hover{transform:translateY(-4px);box-shadow:var(--shadow-md)}
.fm-card__head{display:flex;align-items:flex-start;gap:1rem}
.fm-card__icon-wrap{width:52px;height:52px;border-radius:8px;background:rgba(27,58,45,.1);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.fm-card__icon-wrap :deep(svg){width:26px;height:26px;color:var(--terra)}
.fm-card__title{font-family:var(--fd);font-size:1.3rem;font-weight:600;color:var(--ink);margin-bottom:.4rem;line-height:1.2}
.fm-card__badges{display:flex;flex-wrap:wrap;gap:.35rem}
.fm-badge{font-size:.7rem;padding:.25rem .6rem;border-radius:2px;border:1px solid var(--ink-20);color:var(--ink-60)}
.fm-badge--muted{background:var(--cream-d);border-color:transparent}
.fm-badge--red{background:rgba(200,90,42,.1);color:var(--terra);border-color:rgba(200,90,42,.2)}
.fm-card__desc{font-size:.84rem;color:var(--ink-60);line-height:1.6;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.fm-card__info{display:flex;flex-direction:column;gap:.65rem}
.fm-skills{display:flex;flex-wrap:wrap;gap:.35rem}
.fm-skill{background:var(--cream-d);color:var(--ink-60);font-size:.75rem;padding:.3rem .7rem;border-radius:999px}
.fm-skill--more{color:var(--ink-60)}
.fm-card__actions{display:flex;gap:.6rem;margin-top:auto}
.fm-why-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:1.5rem;margin-top:2rem}
.fm-why-card{background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.1);border-radius:8px;padding:2rem;text-align:center;transition:background .3s ease}
.fm-why-card:hover{background:rgba(255,255,255,.11)}
.fm-why-icon{width:52px;height:52px;border-radius:8px;background:var(--terra);display:flex;align-items:center;justify-content:center;margin:0 auto .85rem}
.fm-why-icon :deep(svg){width:24px;height:24px;color:var(--white)}
.fm-why-card h3{font-family:var(--fd);font-size:1.15rem;font-weight:600;color:var(--cream);margin-bottom:.5rem}
.fm-why-card p{font-size:.84rem;color:rgba(245,239,224,.6);line-height:1.6}
.sf-section-hdr{margin-bottom:2.5rem}
</style>
